"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import styles from "./QrScanner.module.css";
import {
  AlertCircle,
  Backpack,
  ChevronDown,
  ChevronUp,
  CircleArrowLeft,
  Copy,
  ExternalLink,
  Flashlight,
  FlashlightOff,
  SwitchCamera,
  X,
} from "lucide-react";
import { mark_attendance_by_qr } from "../../../Service/ApiServices";
import { useAuth } from "../../../Service/AuthService";
import { useNavigate } from "react-router-dom";

// Custom UI Components
const Button = ({
  children,
  onClick,
  disabled,
  className,
  variant = "default",
  size = "default",
  ...props
}) => {
  const baseClass = styles.button;
  const variantClass = styles[`button-${variant}`] || "";
  const sizeClass = styles[`button-${size}`] || "";
  const disabledClass = disabled ? styles.buttonDisabled : "";

  return (
    <button
      className={`${baseClass} ${variantClass} ${sizeClass} ${disabledClass} ${
        className || ""
      }`}
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
};

const Card = ({ children, className }) => (
  <div className={`${styles.card} ${className || ""}`}>{children}</div>
);

const CardContent = ({ children, className }) => (
  <div className={`${styles.cardContent} ${className || ""}`}>{children}</div>
);

const Badge = ({ children, variant = "default", className }) => (
  <span
    className={`${styles.badge} ${styles[`badge-${variant}`]} ${
      className || ""
    }`}
  >
    {children}
  </span>
);

const QRScannerPage = () => {
  const { token, selectedBranch } = useAuth();
  const navigate = useNavigate();
  // State management
  const [isFlashlightOn, setIsFlashlightOn] = useState(false);
  const [isResultExpanded, setIsResultExpanded] = useState(false);
  const [scanResult, setScanResult] = useState("");
  const [hasResult, setHasResult] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [cameraFacing, setCameraFacing] = useState("environment");
  const [isAttendanceMode, setIsAttendanceMode] = useState(false);

  // Camera management state
  const [availableCameras, setAvailableCameras] = useState([]);
  const [currentCameraId, setCurrentCameraId] = useState("");
  const [permissionGranted, setPermissionGranted] = useState(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  // const [isProcessingFile, setIsProcessingFile] = useState(false)

  const image_url =
    process.env.REACT_APP_IMG_URL || "https://sims.acstechconsulting.com/";
  // Refs
  const html5QrCodeRef = useRef(null);
  // const fileInputRef = useRef(null)
  const scannerElementId = "qr-scanner-container";

  // Loading Overlay Component
  const LoadingOverlay = ({ message }) => (
    <div className={styles.overlay}>
      <div className={styles.spinner} />
      <p className={styles.overlayText}>{message}</p>
    </div>
  );

  // Error Overlay Component
  const ErrorOverlay = ({ message }) => (
    <div className={styles.overlay}>
      <AlertCircle className={styles.errorIcon} />
      <p className={styles.overlayText}>{message}</p>
      <Button
        onClick={() => window.location.reload()}
        className={styles.retryButton}
      >
        Retry
      </Button>
    </div>
  );
  const AttendanceDetailOverlay = ({ detail }) => (
    <div className={styles.overlay}>
      <AlertCircle className={styles.errorIcon} />
      <div style={profilecard}>
        <p className={styles.overlayText}>{detail.message}</p>
        {detail?.user?.image && (
          <div style={{ marginBottom: "7px" }}>
            <img
              src={image_url + detail?.user?.image}
              alt={detail?.user?.name}
              style={{
                width: "100px",
                height: "100px",
                borderRadius: "50%",
                objectFit: "cover",
                border: "2px solid #fff",
              }}
            />
          </div>
        )}
        {detail?.user?.name && (
          <h2 style={{ margin: "5px 0", fontSize: "24px" }}>
            {detail.user.name}
          </h2>
        )}
        {detail?.user?.username && (
          <p style={infoStyle}>{detail.user.username}</p>
        )}
        {detail?.user?.role?.name && (
          <Badge style={infoStyle}>{detail.user.role.name}</Badge>
        )}
        {detail?.user?.school_class && (
          <p style={infoStyle}>{detail.user.school_class}</p>
        )}
        {detail?.user?.section && (
          <p style={infoStyle}>{detail.user.section}</p>
        )}
        {detail?.user?.mobile_no && (
          <p style={infoStyle}>{detail.user.mobile_no}</p>
        )}
        {detail?.user?.school?.name && (
          <p style={infoStyle}>{detail.user.school.name}</p>
        )}

        <Button onClick={clearResult} className={styles.retryButton}>
          ok, Got it
        </Button>
      </div>
    </div>
  );
  const profilecard = {
    color: "white",
    textAlign: "center",
    marginBottom: "2rem",
    background: "rgba(255, 255, 255, 0.2)",
    padding: "2rem",
    borderRadius: "1rem",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
  };
  const infoStyle = {
    margin: "4px 0",
    fontSize: "clamp(9px, 2vw, 13px)",
    color: "#f0f0f0",
  };

  // Initialize scanner
  const initializeScanner = useCallback(async () => {
    try {
      setIsLoading(true);
      setError("");

      // Dynamic import for html5-qrcode
      const { Html5Qrcode } = await import("html5-qrcode");

      // Create scanner instance
      const scanner = new Html5Qrcode(scannerElementId);
      html5QrCodeRef.current = scanner;
      scanner.styles = {
        display: "flex",
      };

      // Get available cameras
      const cameras = await Html5Qrcode.getCameras();
      setAvailableCameras(cameras);

      if (cameras.length === 0) {
        throw new Error("No cameras found on this device");
      }

      // Find preferred camera (environment facing) or use first available
      const preferredCamera =
        cameras.find(
          (camera) =>
            camera.label.toLowerCase().includes("back") ||
            camera.label.toLowerCase().includes("environment")
        ) || cameras[0];

      setCurrentCameraId(preferredCamera.id);
      setPermissionGranted(true);
    } catch (err) {
      console.error("Scanner initialization failed:", err);
      setError(err.message || "Failed to initialize camera");
      setPermissionGranted(false);
    } finally {
      setIsLoading(false);
    }
  }, []);
  // =============attandance========================
  const [attendanceloading, setAttendanceLoading] = useState(false); // State to show the loading spinner
  const [attendanceData, setAttendanceData] = useState(""); // State to show the loading spinner

  useEffect(() => {
    if (!scanResult || !isAttendanceMode) return;
    // Step 1: Get user's geolocation
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }

    setAttendanceLoading(true);
    navigator.geolocation.getCurrentPosition((position) => {
      const { latitude, longitude } = position.coords;

      const data = {
        latitude,
        longitude,
      };
      const url = new URL(scanResult);
      const qr = url.searchParams.get("qr");
      mark_attendance_by_qr(qr, data, token, selectedBranch)
        .then((res) => {
          if (res.status) {
            const data = res?.response;

            setAttendanceData(data);
          } else {
            setError(res?.response?.data?.message || "Failed to mark attendance");
          }
        })
        .finally(() => setAttendanceLoading(false));
    });
  }, [scanResult]);
  // Start scanning
  const startScanning = useCallback(async () => {
    if (!html5QrCodeRef.current || !currentCameraId || isScanning) return;

    try {
      setError("");

      const config = {
        fps: 10,
        rememberLastUsedCamera: true,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0,
        display: "flex",
      };

      await html5QrCodeRef.current.start(
        currentCameraId,
        config,
        // Success callback
        (decodedText) => {
          setScanResult(decodedText);
          setHasResult(true);
          setIsResultExpanded(true);

          setIsScanning(false);
          html5QrCodeRef.current?.pause(true);
        },
        // Error callback
        (errorMessage) => {
          if (!errorMessage.includes("No QR code found")) {
            console.warn("QR scan error:", errorMessage);
          }
        }
      );

      setIsScanning(true);
    } catch (err) {
      console.error("Failed to start scanning:", err);
      setError(err.message || "Failed to start camera");
    }
  }, [currentCameraId, isScanning]);

  // Stop scanning
  const stopScanning = useCallback(async () => {
    if (!html5QrCodeRef.current || !isScanning) return

    try {
      await html5QrCodeRef.current.stop()
      setIsScanning(false)
    } catch (err) {
      console.error("Failed to stop scanning:", err)
    }
  }, [isScanning])
const handlebackButtonClick = async () => {
    
      await html5QrCodeRef.current.stop()
   
      navigate(-1); // Navigate back if not scanning
  
  }

  // Toggle flashlight
  const toggleFlashlight = useCallback(async () => {
    if (!html5QrCodeRef.current || !isScanning) return;

    try {
      const capabilities = html5QrCodeRef.current.getRunningTrackCapabilities();

      if (!capabilities.torch) {
        setError("Flashlight not supported on this device");
        return;
      }

      await html5QrCodeRef.current.applyVideoConstraints({
        torch: !isFlashlightOn,
      });

      setIsFlashlightOn(!isFlashlightOn);
    } catch (err) {
      console.error("Failed to toggle flashlight:", err);
      setError("Failed to control flashlight");
    }
  }, [isFlashlightOn, isScanning]);

  // Flip camera
  const flipCamera = useCallback(async () => {
    if (availableCameras.length < 2) return;

    try {
      setError("");

      if (html5QrCodeRef.current && isScanning) {
        await html5QrCodeRef.current.stop();
        setIsScanning(false);
      }

      const currentIndex = availableCameras.findIndex(
        (cam) => cam.id === currentCameraId
      );
      const nextIndex = (currentIndex + 1) % availableCameras.length;
      const nextCamera = availableCameras[nextIndex];

      setCurrentCameraId(nextCamera.id);

      const isBackCamera =
        nextCamera.label.toLowerCase().includes("back") ||
        nextCamera.label.toLowerCase().includes("environment");
      setCameraFacing(isBackCamera ? "environment" : "user");

      setIsFlashlightOn(false);

      setTimeout(() => {
        if (!hasResult) {
          startScanning();
        }
      }, 500);
    } catch (err) {
      console.error("Failed to flip camera:", err);
      setError("Failed to switch camera");
    }
  }, [availableCameras, currentCameraId, isScanning, hasResult, startScanning]);

  // Handle file upload
  // const handleFileUpload = useCallback(
  //   async (event) => {
  //     const file = event.target.files?.[0]
  //     if (!file) return

  //     try {
  //       setIsProcessingFile(true)
  //       setError("")

  //       const { Html5Qrcode } = await import("html5-qrcode")
  //       const result = await Html5Qrcode.scanFile(file, true)

  //       setScanResult(result)
  //       setHasResult(true)
  //       setIsScanning(false)

  //       if (html5QrCodeRef.current && isScanning) {
  //         await html5QrCodeRef.current.stop()
  //       }
  //     } catch (err) {
  //       console.error("Failed to scan file:", err)
  //       setError("No QR code found in the selected image")
  //     } finally {
  //       setIsProcessingFile(false)
  //       if (fileInputRef.current) {
  //         fileInputRef.current.value = ""
  //       }
  //     }
  //   },
  //   [isScanning],
  // )

  // Open gallery
  // const openGallery = useCallback(() => {
  //   fileInputRef.current?.click()
  // }, [])
  useEffect(() => {
    clearResult();
  }, [isAttendanceMode]);

  // Clear result
  const clearResult = useCallback(async () => {
    setHasResult(false);
    setIsResultExpanded(false);
    setScanResult("");
    setError("");
    setAttendanceData("");

    if (html5QrCodeRef.current && currentCameraId) {
      try {
        if (!isScanning) {
          html5QrCodeRef.current.resume();
          setIsScanning(true);
        }
      } catch (err) {
        try {
          await startScanning();
        } catch (restartErr) {
          console.error("Failed to restart scanning:", restartErr);
          setError("Failed to restart camera");
        }
      }
    }
  }, [isScanning, startScanning, currentCameraId]);

  // Utility functions
  const toggleResultSection = () => setIsResultExpanded(!isResultExpanded);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(scanResult);
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
    }
  };

  const getResultType = (result) => {
    if (result.startsWith("http://") || result.startsWith("https://"))
      return "URL";
    if (result.startsWith("mailto:")) return "Email";
    if (result.startsWith("tel:")) return "Phone";
    if (result.startsWith("WIFI:")) return "WiFi";
    return "Text";
  };

  // Effects
  useEffect(() => {
    initializeScanner();

    return () => {
      if (html5QrCodeRef?.current) {
        html5QrCodeRef.current.clear();
      }
    };
  }, [initializeScanner]);

  useEffect(() => {
    if (currentCameraId && !hasResult && !isScanning) {
      startScanning();
    }
  }, [currentCameraId, hasResult, isScanning, startScanning]);

  return (
    <div className={styles.container}>
      {/* Scanner root */}
      <div id={scannerElementId} className={styles.scannerContainer} />

      {/* Hidden file input */}
      {/* <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileUpload}
        className={styles.hiddenInput}
      /> */}

      {/* Loading/Error overlays */}
      {isLoading && <LoadingOverlay message={"Initializing camera…"} />}
      {isAttendanceMode && attendanceloading && (
        <LoadingOverlay message={"Marking Attendance...."} />
      )}
      {!isLoading && (error || permissionGranted === false) && (
        <ErrorOverlay
          message={error || "Please allow camera access to scan QR codes"}
        />
      )}
      {isAttendanceMode && attendanceData && (
        <AttendanceDetailOverlay detail={attendanceData} />
      )}
      {/* {isProcessingFile && (
        <div className={styles.processingOverlay}>
          <div className={styles.spinner} />
          <p className={styles.overlayText}>Processing image...</p>
        </div>
      )} */}

      {/* Scanning frame overlay */}
      {/* <div className={styles.frameOverlay}>
        <div className={styles.scanFrame}> */}
      {/* <div className={`${styles.corner} ${styles.topLeft}`} />
          <div className={`${styles.corner} ${styles.topRight}`} />
          <div className={`${styles.corner} ${styles.bottomLeft}`} />
          <div className={`${styles.corner} ${styles.bottomRight}`} /> */}

      {/* {isScanning && (
            <div className={styles.scanLines}>
              <div className={styles.scanLine} />
              <div className={`${styles.scanLine} ${styles.scanLineDelay}`} />
            </div>
          )} */}
      {/* </div>
      </div> */}

      {/* Top controls */}
        <Button
            variant="ghost"
            size="icon"
            className={styles.backButton}
            onClick={handlebackButtonClick}
          >
            <CircleArrowLeft className={styles.icon} />
          </Button>
      
      <div className={styles.topControls}>
        <div className={styles.statusSection}>
          <div
            className={`${styles.statusDot} ${
              isScanning ? styles.statusActive : ""
            }`}
          />
          <span className={styles.statusText}>
            {isAttendanceMode
              ? isScanning
                ? "Marking Attendance..."
                : hasResult
                ? "Attendance Marked"
                : "Ready for Attendance"
              : isScanning
              ? "Scanning..."
              : hasResult
              ? "Scan Complete"
              : "Ready"}
          </span>

        </div>

        <div className={styles.topButtons}>
          <Button
            variant="ghost"
            size="sm"
            className={`${styles.modeToggle} ${
              isAttendanceMode ? styles.modeToggleActive : ""
            }`}
            onClick={() => setIsAttendanceMode(!isAttendanceMode)}
          >
            {isAttendanceMode ? "Attendance" : "QR Scan"}
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className={styles.iconButton}
            onClick={flipCamera}
            disabled={availableCameras.length < 2}
          >
            <SwitchCamera className={styles.icon} />
          </Button>
        </div>
      </div>

      {/* Side controls */}
      <div className={styles.sideControls}>
        {cameraFacing === "environment" && (
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleFlashlight}
            disabled={availableCameras.length < 2}
            className={`${styles.iconButton} ${
              isFlashlightOn ? styles.flashlightActive : ""
            }`}
          >
            {isFlashlightOn ? (
              <Flashlight className={styles.icon} />
            ) : (
              <FlashlightOff className={styles.icon} />
            )}
          </Button>
        )}
      </div>

      {/* Error display */}
      {error && (
        <div className={styles.errorDisplay}>
          <div className={styles.errorMessage}>{error}</div>
        </div>
      )}

      {/* Result section */}
      {hasResult && !isAttendanceMode && (
        <div
          className={`${styles.resultSection} ${
            isResultExpanded ? styles.resultExpanded : ""
          }`}
        >
          <Card className={styles.resultCard}>
            <CardContent className={styles.resultContent}>
              {/* Handle bar */}
              <div className={styles.handleBar} onClick={toggleResultSection}>
                {isResultExpanded ? <ChevronDown /> : <ChevronUp />}
              </div>

              {/* Result header */}
              <div className={styles.resultHeader}>
                <div className={styles.resultTitle}>
                  <div className={styles.resultDot} />
                  <span className={styles.resultTitleText}>
                    {isAttendanceMode
                      ? "Attendance Recorded"
                      : "QR Code Detected"}
                  </span>
                </div>
                <div className={styles.resultActions}>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={clearResult}
                    className={styles.resultActionButton}
                  >
                    Re scan
                  </Button>
                </div>
              </div>

              <Badge
                variant="secondary"
                style={{
                  backgroundColor: "var('--qrcodedetailback-color')",
                  color: "var(--qrcodedetailtext-color)",
                }}
              >
                {isAttendanceMode ? "Attendance" : getResultType(scanResult)}
              </Badge>

              {/* Result content */}
              <div className={styles.resultData}>
                <p className={styles.resultText}>{scanResult}klklklkkl</p>
              </div>

              {/* Action buttons */}
              <div className={styles.actionButtons}>
                <Button
                  onClick={copyToClipboard}
                  variant="outline"
                  className={styles.actionButton}
                >
                  <Copy className={styles.buttonIcon} />
                  Copy
                </Button>
                <Button
                  className={styles.actionButton}
                  onClick={() => {
                    if (scanResult.startsWith("http")) {
                      window.open(scanResult, "_blank");
                    }
                  }}
                >
                  <ExternalLink className={styles.buttonIcon} />
                  Open
                </Button>
              </div>
              <div class></div>
              {/* Expanded content */}
              {isResultExpanded && (
                <div className={styles.expandedContent}>
                  <h3 className={styles.expandedTitle}>Scan Details</h3>
                  <div className={styles.detailsList}>
                    <div className={styles.detailItem}>
                      <span>Type:</span>
                      <span>{getResultType(scanResult)}</span>
                    </div>
                    <div className={styles.detailItem}>
                      <span>Format:</span>
                      <span>QR Code</span>
                    </div>
                    <div className={styles.detailItem}>
                      <span>Length:</span>
                      <span>{scanResult.length} characters</span>
                    </div>
                    <div className={styles.detailItem}>
                      <span>Camera:</span>
                      <span>
                        {cameraFacing === "environment" ? "Back" : "Front"}
                      </span>
                    </div>
                    <div className={styles.detailItem}>
                      <span>Scanned:</span>
                      <span>Just now</span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Instructions */}
      {!hasResult && isScanning && (
        <div className={styles.instructions}>
          <div className={styles.instructionsContent}>
            <p className={styles.instructionText}>
              Position QR code within the frame
            </p>
            <p className={styles.instructionSubtext}>
              Scanning will happen automatically
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRScannerPage;
